/* Shared, DOM-free rules for product selection and configured cart validation. */
(function (root) {
  'use strict';
  const list = value => Array.isArray(value) ? value : [];
  function source(names, product, variant, indexes) {
    const explicit = list(indexes).filter(index => Number.isInteger(index) && index >= 0);
    if (explicit.length > 1) return undefined;
    if (explicit.length === 1) return variant ? variant.options[explicit[0]] : undefined;
    const found = list(product.options).reduce((matches, name, index) =>
      list(names).includes(name) ? matches.concat(index) : matches, []);
    return found.length === 1 && variant ? variant.options[found[0]] : undefined;
  }
  function resolve(choice, product, variant) {
    const hidden = { eligible: false, variant: null };
    if (!variant) return hidden;
    const names = list(choice.visibilityOptionNames), values = list(choice.visibleForValues);
    if (names.length || values.length) {
      const value = source(names, product, variant, choice.visibilityOptionIndexes);
      if (!names.length || !values.length || value === undefined) return { ...hidden, error: 'configuration' };
      if (!values.includes(value)) return hidden;
    }
    let accessory;
    if (choice.mode === 'fixed') {
      if (list(choice.mappings).length || list(choice.mappingOptionNames).length) return { ...hidden, error: 'configuration' };
      accessory = choice.fixedVariant;
    } else if (choice.mode === 'mapped') {
      if (choice.fixedVariant) return { ...hidden, error: 'configuration' };
      const value = source(choice.mappingOptionNames, product, variant, choice.mappingOptionIndexes);
      if (value === undefined || !list(choice.mappings).length) return { ...hidden, error: 'configuration' };
      const seen = new Set();
      for (const mapping of choice.mappings) {
        if (!list(mapping.values).length) return { ...hidden, error: 'configuration' };
        for (const item of mapping.values) {
          if (seen.has(item)) return { ...hidden, error: 'configuration' };
          seen.add(item);
        }
      }
      const matches = choice.mappings.filter(mapping => list(mapping.values).includes(value));
      if (!matches.length) return hidden;
      accessory = matches[0].variant;
    } else return { ...hidden, error: 'configuration' };
    if (!accessory?.id || accessory.id === variant.id) return { ...hidden, error: 'configuration' };
    return { eligible: true, variant: accessory, available: accessory.available === true };
  }
  function validate(data, variant, selections) {
    const errors = [];
    if (!variant?.available) errors.push({ message: 'deskUnavailable' });
    if (data.error || !list(data.groups).length) errors.push({ message: 'configuration' });
    const groupIds = new Set();
    for (const group of list(data.groups)) {
      const error = message => errors.push({ group: group.id, message });
      if (groupIds.has(group.id) || !group.id || !group.heading ||
          !['dropdown', 'image_choices'].includes(group.presentation) ||
          typeof group.required !== 'boolean' || typeof group.allowNone !== 'boolean' ||
          (group.allowNone && !group.noneLabel) || !list(group.choices).length) error('configuration');
      groupIds.add(group.id);
      const ids = list(group.choices).map(choice => choice.id);
      if (new Set(ids).size !== ids.length) error('configuration');
      for (const choice of list(group.choices)) {
        if (!choice.id || !choice.label || resolve(choice, data.product, variant).error) error('configuration');
      }
      const selected = selections[group.id];
      if (selected == null) { if (group.required) error('required'); continue; }
      if (selected === 'none') { if (!group.allowNone) error('required'); continue; }
      const choice = group.choices.find(item => item.id === selected);
      if (!choice) { error('unavailable'); continue; }
      const resolved = resolve(choice, data.product, variant);
      if (!resolved.eligible || !resolved.available) error('unavailable');
    }
    return errors;
  }
  function buildItems(data, variant, selections, quantity, configuration, properties = {}) {
    if (!Number.isSafeInteger(quantity) || quantity < 1) throw new Error('quantity');
    if (!configuration || validate(data, variant, selections).length) throw new Error('selection');
    const parent = { id: variant.id, quantity, properties: { ...properties,
      _desky_configuration: configuration, _desky_role: 'parent' } };
    const children = [], expected = {};
    data.groups.forEach(group => {
      const selected = selections[group.id];
      if (selected == null) return;
      if (selected === 'none') {
        parent.properties[group.heading] = group.noneLabel;
        return;
      }
      const choice = group.choices.find(item => item.id === selected);
      const accessory = resolve(choice, data.product, variant).variant;
      expected[group.id] = accessory.id;
      // Pre-order accessories carry their own date, like the main product line: translated visible label
      // plus the stable hidden `_preorder` key (read by Flow and preorder-validation).
      children.push({ id: accessory.id, quantity, parent_id: variant.id, properties: {
        ...(accessory.preorder ? { [root.deskyPreorderLabel || 'Preorder']: accessory.preorder, _preorder: accessory.preorder } : {}),
        _desky_configuration: configuration, _desky_role: 'child', _desky_group: group.id
      } });
    });
    parent.properties._desky_expected = JSON.stringify(expected);
    return [parent, ...children];
  }
  function cartProblems(cart) {
    const problems = [];
    const items = list(cart.items), parents = items.filter(item => item.properties?._desky_role === 'parent');
    for (const parent of parents) {
      let expected;
      try { expected = JSON.parse(parent.properties._desky_expected); } catch (_) { problems.push(parent.key); continue; }
      if (!expected || typeof expected !== 'object' || Array.isArray(expected)) { problems.push(parent.key); continue; }
      const children = items.filter(item => item.properties?._desky_role === 'child' &&
        item.properties._desky_configuration === parent.properties._desky_configuration);
      if (parents.filter(item => item.properties._desky_configuration === parent.properties._desky_configuration).length !== 1 ||
          children.length !== Object.keys(expected).length || children.some(child =>
            child.parent_relationship?.parent_key !== parent.key || child.quantity !== parent.quantity ||
            Number(expected[child.properties._desky_group]) !== child.variant_id) ||
          Object.keys(expected).some(group => children.filter(child => child.properties._desky_group === group).length !== 1)) {
        problems.push(parent.key);
      }
    }
    for (const child of items.filter(item => item.properties?._desky_role === 'child')) {
      if (!parents.some(parent => parent.key === child.parent_relationship?.parent_key &&
        parent.properties._desky_configuration === child.properties._desky_configuration)) problems.push(child.key);
    }
    return [...new Set(problems)];
  }
  const api = { resolve, validate, buildItems, cartProblems };
  if (typeof module !== 'undefined' && module.exports) module.exports = api;
  else root.DeskyAccessoriesCore = api;
})(typeof window === 'undefined' ? globalThis : window);
